import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useSidebar } from '../context/SidebarContext'
import type { CrmPage } from './crmBridge'
import { railPageOf, railPages, type CrmPermissions } from './crmRail'
import { RAIL_ICONS } from './crmRailIcons'
import './CrmPageRail.css'

interface Props {
  accountId: string
  page: CrmPage
  perms: CrmPermissions | null
  unread?: number
  /** Opens More as OnlyFans' popup over the page instead of going to /crm/more; given the rail's right edge. */
  onMore?: (railRight: number) => void
  moreOpen?: boolean
}

/**
 * The CRM page rail (ADR 0023): the console's copy of the CRM Sidebar, shown on
 * every account CRM route. React pages render beside it; the rest are the frame.
 */
export function CrmPageRail({ accountId, page, perms, unread = 0, onMore, moreOpen = false }: Props) {
  const { t } = useTranslation('library')
  const base = `/account/${encodeURIComponent(accountId)}/crm`
  const { sidebarWidth } = useSidebar()

  return (
    <nav className="crm-rail" style={{ left: sidebarWidth }} aria-label={t('crmRail.label')}>
      {railPages(perms).map((p) => {
        if (p.page === 'more' && onMore) {
          const on = moreOpen || p.page === railPageOf(page)
          return (
            <button
              key={p.page}
              type="button"
              className={`crm-rail-item ${on ? 'is-on' : ''}`}
              title={t(p.labelKey)}
              aria-label={t(p.labelKey)}
              aria-haspopup="dialog"
              aria-expanded={moreOpen}
              onClick={(e) => onMore((e.currentTarget.parentElement ?? e.currentTarget).getBoundingClientRect().right)}
            >
              <RailGlyph id={p.icon} />
            </button>
          )
        }
        const on = !moreOpen && p.page === railPageOf(page)
        const badge = p.page === 'chats' && unread > 0 ? (unread > 99 ? '99+' : String(unread)) : null
        return (
          <Link
            key={p.page}
            to={`${base}/${p.page}`}
            className={`crm-rail-item ${on ? 'is-on' : ''}`}
            title={t(p.labelKey)}
            aria-label={t(p.labelKey)}
            aria-current={on ? 'page' : undefined}
          >
            <RailGlyph id={p.icon} />
            {badge && <span className="crm-rail-badge">{badge}</span>}
          </Link>
        )
      })}
    </nav>
  )
}

export function RailGlyph({ id }: { id: string }) {
  const icon = RAIL_ICONS[id]
  if (!icon) return null
  // Static markup copied from the CRM's own sprite, not user data.
  return (
    <svg className="crm-rail-icon" viewBox={icon.viewBox} fill="currentColor" aria-hidden="true" dangerouslySetInnerHTML={{ __html: icon.body }} />
  )
}
